import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  EyeIcon,
  ShieldCheckIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  listPendingVerifications,
  getVerificationDetail,
  reviewSupplierVerification,
} from '../api/superAdminApi';

const MarketplaceVerificationPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listPendingVerifications();
      setSuppliers(response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleViewDetail = async (id: string | number) => {
    try {
      setDetailLoading(true);
      setActionError(null);
      const response = await getVerificationDetail(Number(id));
      const detail = response.data?.profile || response.profile || response.data || response;
      setSelected(detail);
    } catch (err: any) {
      setActionError(err.message || 'Failed to load supplier details. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (id: string | number) => {
    try {
      setActionLoading(String(id));
      setActionError(null);
      await reviewSupplierVerification(Number(id), 'approved');
      setSelected(null);
      setActionSuccess('Supplier approved and marketplace access granted.');
      fetchData();
    } catch (err: any) {
      setActionError(err.message || 'Failed to approve supplier. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) return;
    try {
      setActionLoading(String(selected.id));
      setActionError(null);
      await reviewSupplierVerification(Number(selected.id), 'rejected', rejectReason);
      setSelected(null);
      setShowRejectModal(false);
      setRejectReason('');
      setActionSuccess('Supplier registration rejected.');
      fetchData();
    } catch (err: any) {
      setActionError(err.message || 'Failed to reject supplier. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      submitted: 'bg-amber-100 text-amber-700 border-amber-200',
      under_review: 'bg-blue-100 text-blue-700 border-blue-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      suspended: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return styles[status] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <ShieldCheckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Supplier Verification</h1>
              <p className="text-emerald-100 mt-1">Review and approve supplier marketplace access</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Action feedback (replaces silent console-only failures) */}
      {actionError && (
        <div role="alert" className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700 text-sm font-medium">{actionError}</p>
            <button onClick={() => setActionError(null)} className="mt-1 text-xs font-semibold text-red-600 hover:underline">Dismiss</button>
          </div>
        </div>
      )}
      {actionSuccess && (
        <div role="status" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3">
          <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-emerald-700 text-sm font-medium">{actionSuccess}</p>
            <button onClick={() => setActionSuccess(null)} className="mt-1 text-xs font-semibold text-emerald-600 hover:underline">Dismiss</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto">
            <CheckCircleIcon className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">All caught up!</h3>
          <p className="text-gray-500 mt-2">No suppliers pending verification</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Supplier List */}
          <div className="lg:col-span-2 space-y-3">
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-1">{suppliers.length} pending</p>
            {suppliers.map((s: any) => (
              <button
                key={s.id}
                onClick={() => handleViewDetail(s.id)}
                disabled={detailLoading}
                className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${
                  selected?.id === s.id
                    ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-md">
                      <BuildingOfficeIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{s.business_name || s.name || s.tenant_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.contact_email || s.email || ''}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadge(s.verification_status || s.status || 'submitted')}`}>
                    {(s.verification_status || s.status || 'submitted').replace('_', ' ')}
                  </span>
                  {s.country && <span className="text-xs text-gray-400">{s.country}</span>}
                  {s.submitted_at && <span className="text-xs text-gray-400">• {new Date(s.submitted_at).toLocaleDateString()}</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3">
            {detailLoading ? (
              <div className="flex items-center justify-center h-80 bg-white rounded-2xl border border-gray-100 shadow-lg">
                <div className="relative">
                  <div className="w-10 h-10 border-4 border-emerald-200 rounded-full"></div>
                  <div className="w-10 h-10 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
              </div>
            ) : selected ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
                {/* Detail Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-5 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <BuildingOfficeIcon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-extrabold text-gray-900">{selected.business_name || selected.name || selected.tenant_name}</h2>
                        <p className="text-sm text-gray-500 mt-0.5">{selected.contact_email || selected.email} • {selected.contact_phone || selected.phone || ''}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                      <XMarkIcon className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Description */}
                  {selected.description && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Description</h4>
                      <p className="text-sm text-gray-700 leading-relaxed">{selected.description}</p>
                    </div>
                  )}

                  {/* Documents */}
                  {selected.documents && selected.documents.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Documents</h4>
                      <div className="space-y-2">
                        {selected.documents.map((doc: any, idx: number) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-700 flex-1">{doc.name || doc.type || `Document ${idx + 1}`}</span>
                            {doc.url && (
                              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors">
                                <EyeIcon className="w-4 h-4 text-blue-600" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Capabilities */}
                  {selected.requested_capabilities && selected.requested_capabilities.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Requested Capabilities</h4>
                      <div className="flex flex-wrap gap-2">
                        {selected.requested_capabilities.map((cap: string) => (
                          <span key={cap} className="px-3 py-1.5 bg-violet-50 text-violet-700 rounded-xl text-xs font-semibold border border-violet-200">
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Review History */}
                  {selected.reviews && selected.reviews.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Review History</h4>
                      <div className="space-y-2">
                        {selected.reviews.map((review: any, idx: number) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                review.decision === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>{review.decision}</span>
                              <span className="text-xs text-gray-400">{review.reviewed_at}</span>
                            </div>
                            {review.notes && <p className="text-sm text-gray-600 mt-2">{review.notes}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {(selected.verification_status || selected.status) === 'submitted' && (
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleApprove(selected.id)}
                        disabled={actionLoading === String(selected.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 font-bold shadow-lg shadow-emerald-200 transition-all"
                      >
                        <CheckIcon className="w-5 h-5" />
                        {actionLoading === String(selected.id) ? 'Processing...' : 'Approve Supplier'}
                      </button>
                      <button
                        onClick={() => setShowRejectModal(true)}
                        disabled={actionLoading === String(selected.id)}
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 font-bold shadow-lg shadow-red-200 transition-all"
                      >
                        <XMarkIcon className="w-5 h-5" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
                <BuildingOfficeIcon className="w-16 h-16 text-gray-200 mx-auto" />
                <p className="text-gray-400 mt-4 font-medium">Select a supplier to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Reject Supplier</h3>
            <p className="text-sm text-gray-500">Please provide a reason for rejection.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none"
              rows={4}
              placeholder="Reason for rejection..."
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading !== null}
                className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 font-bold text-sm"
              >
                {actionLoading ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceVerificationPage;
